import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EvaluationMatrix, { MatrixRow } from '../components/EvaluationMatrix';
import { authService } from '../services/authService';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const AdminAIReportPage: React.FC = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnalytics = async () => {
            const data = await authService.getSystemAnalytics();
            if (data) {
                setAnalytics(data);
            }
            setLoading(false);
        };
        fetchAnalytics();
    }, []);

    // --- MOCK DATA FOR MATRICES (Static Eval) ---
    const questionGenData: MatrixRow[] = [
        { dimension: 'Academic Accuracy', metric: '% correct answers', howMeasured: 'Expert / teacher review', thresholdOrTarget: '≥ 99%', status: 'pass' },
        { dimension: 'Grade Alignment', metric: 'Bloom’s taxonomy match', howMeasured: 'Rubric-based audit', thresholdOrTarget: '≥ 95%', status: 'pass' },
        { dimension: 'Concept Coverage', metric: 'Curriculum coverage', howMeasured: 'Curriculum mapping', thresholdOrTarget: '≥ 90%', status: 'warning' },
        { dimension: 'Diversity', metric: 'Variety of questions', howMeasured: 'Duplicate rate', thresholdOrTarget: '< 10%', status: 'pass' },
        { dimension: 'Safety', metric: 'Inappropriate content', howMeasured: 'Automated + human', thresholdOrTarget: '0 tolerance', status: 'pass' },
    ];

    const adaptiveEngineData: MatrixRow[] = [
        { dimension: 'Placement Accuracy', metric: 'Level match', howMeasured: 'Diagnostic vs outcome', thresholdOrTarget: '≥ 85%', status: 'pass' },
        { dimension: 'Progression Smoothness', metric: 'Sudden jumps', howMeasured: 'Difficulty delta analysis', thresholdOrTarget: '< 1 level/session', status: 'pass' },
        { dimension: 'Learning Gain', metric: 'Pre vs post score', howMeasured: 'A/B cohort study', thresholdOrTarget: '+20%', status: 'pass' },
        { dimension: 'Frustration Signals', metric: 'Quit / skip rate', howMeasured: 'Behavioral', thresholdOrTarget: '< 10%', status: 'fail' },
    ];

    const explanationData: MatrixRow[] = [
        { dimension: 'Clarity', metric: 'Child comprehension', howMeasured: 'Child testing', thresholdOrTarget: '≥ 4/5', status: 'pass' },
        { dimension: 'Vocabulary Fit', metric: 'Grade-level language', howMeasured: 'Readability score', thresholdOrTarget: 'On-grade', status: 'pass' },
        { dimension: 'Correctness', metric: 'Concept explanation', howMeasured: 'Teacher review', thresholdOrTarget: '99%', status: 'pass' },
        { dimension: 'Encouragement Tone', metric: 'Positive framing', howMeasured: 'NLP sentiment', thresholdOrTarget: '100%', status: 'pass' },
        { dimension: 'Hint Quality', metric: 'Helps without giving answer', howMeasured: 'Observed trials', thresholdOrTarget: '≥ 80% success', status: 'warning' },
    ];

    const engagementData: MatrixRow[] = [
        { dimension: 'Session Completion', metric: 'Finishes lesson', howMeasured: 'Analytics', thresholdOrTarget: '≥ 70%', status: 'pass' },
        { dimension: 'Return Rate (D7)', metric: 'Habit building', howMeasured: 'Retention', thresholdOrTarget: '≥ 40%', status: 'pass' },
        { dimension: 'Voluntary Usage', metric: 'Non-forced play', howMeasured: 'Session Type', thresholdOrTarget: '≥ 50%', status: 'pass' },
        { dimension: 'Time-on-Task', metric: 'Focus quality', howMeasured: 'Grade-adjusted', thresholdOrTarget: 'N/A', status: 'pass' },
        { dimension: 'Rage Clicks', metric: 'Confusion', howMeasured: 'Interaction log', thresholdOrTarget: '< 2/session', status: 'pass' },
    ];

    const safetyData: MatrixRow[] = [
        { dimension: 'COPPA Compliance', metric: 'Data audit', howMeasured: 'Mandatory', thresholdOrTarget: 'Pass', status: 'pass' },
        { dimension: 'PII Leakage', metric: 'Red-team prompts', howMeasured: 'Zero tolerance', thresholdOrTarget: '0', status: 'pass' },
        { dimension: 'Bias', metric: 'Name / culture tests', howMeasured: 'No disparity', thresholdOrTarget: 'Pass', status: 'pass' },
        { dimension: 'Hallucinations', metric: 'Fact-check eval', howMeasured: 'Zero in learning content', thresholdOrTarget: '0', status: 'pass' },
        { dimension: 'Manipulation', metric: 'Reward pressure tests', howMeasured: 'None', thresholdOrTarget: 'None', status: 'pass' },
    ];

    const reliabilityData: MatrixRow[] = [
        { dimension: 'Latency (P95)', metric: '< 1.5s', howMeasured: 'APM', thresholdOrTarget: '< 1.5s', status: 'pass' },
        { dimension: 'Error Rate', metric: '< 0.5%', howMeasured: 'Logs', thresholdOrTarget: '< 0.5%', status: 'pass' },
        { dimension: 'Offline Grace', metric: 'Fallback enabled', howMeasured: 'Client config', thresholdOrTarget: 'Enabled', status: 'pass' },
        { dimension: 'Consistency', metric: 'Re-run variance', howMeasured: 'Test Suite', thresholdOrTarget: '< 1%', status: 'pass' },
    ];

    // --- CHART DATA ---
    // Use last 7 days from recentActivity or fallback to empty
    const recentActivity = analytics?.recentActivity || [];
    // Ensure we have 7 days of labels, even if no data (reverse order for chart left-to-right)
    const labels = recentActivity.length > 0
        ? recentActivity.map((r: any) => new Date(r.date).toLocaleDateString('en-US', { weekday: 'short' })).reverse()
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Map data to chart (reverse to chronological)
    const quizCounts = recentActivity.map((r: any) => parseInt(r.quiz_count)).reverse();
    // Daily active users data
    const activeUsers = (analytics?.dailyActiveUsers || []).map((r: any) => parseInt(r.active_users)).reverse();

    const utilizationChartData = {
        labels,
        datasets: [
            {
                fill: true,
                label: 'Daily AI Interactions (Questions Generated)',
                data: quizCounts.length ? quizCounts : [0, 0, 0, 0, 0, 0, 0],
                borderColor: 'rgb(99, 102, 241)',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                tension: 0.4,
            },
            {
                fill: true,
                label: 'Active Student Users',
                data: activeUsers.length ? activeUsers : [0, 0, 0, 0, 0, 0, 0],
                borderColor: 'rgb(244, 63, 94)',
                backgroundColor: 'rgba(244, 63, 94, 0.1)',
                tension: 0.4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: false,
                text: 'Daily AI Utilization',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                suggestedMax: 10,
            }
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 bg-slate-50 min-h-screen">

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center">
                    <button
                        onClick={() => navigate('/admin')}
                        className="mr-4 p-2 rounded-full hover:bg-slate-200 text-slate-500 transition-colors"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-indigo-900 brand-font">Weekly AI Report</h1>
                        <p className="text-slate-500">System performance, safety, and engagement metrics</p>
                    </div>
                </div>
                <div className="flex space-x-3">
                    <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-bold text-sm flex items-center">
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                        System Healthy
                    </span>
                    <button className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg font-medium hover:bg-slate-50 shadow-sm">
                        <i className="fas fa-download mr-2"></i> Export PDF
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-slate-500">Loading analytics...</div>
            ) : (
                <>
                    {/* Utilization Chart Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-10">
                        <h2 className="text-xl font-bold text-slate-800 mb-6">Daily AI Utilization</h2>
                        <div className="h-80 w-full">
                            <Line options={chartOptions} data={utilizationChartData} />
                        </div>
                    </div>

                    {/* Feature-Level Eval Matrix (Core) */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        <EvaluationMatrix
                            title="AI Question Generation"
                            description="Core content generation accuracy and safety"
                            columns={['Dimension', 'Metric', 'How Measured', 'Threshold']}
                            data={questionGenData}
                            colorTheme="indigo"
                        />

                        <EvaluationMatrix
                            title="Adaptive Difficulty Engine"
                            description="Progression and personalized learning efficacy"
                            columns={['Dimension', 'Metric', 'Measurement', 'Target']}
                            data={adaptiveEngineData}
                            colorTheme="purple"
                        />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        <EvaluationMatrix
                            title="Explanations & Feedback"
                            description="Student-facing content quality"
                            columns={['Dimension', 'Metric', 'How', 'Target']}
                            data={explanationData}
                            colorTheme="blue"
                        />

                        <EvaluationMatrix
                            title="Engagement Evaluation"
                            description="Critical metrics for kids apps"
                            columns={['Metric', 'Meaning', 'How Measured', 'Benchmark']}
                            data={engagementData}
                            colorTheme="amber"
                        />
                    </div>

                    {/* Safety & Reliability */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        <EvaluationMatrix
                            title="Safety, Compliance & Ethics"
                            description="Non-negotiable safety standards"
                            columns={['Area', 'Metric/Method', 'Rule', 'Status']}
                            data={safetyData}
                            colorTheme="rose"
                        />

                        <EvaluationMatrix
                            title="Reliability & Performance"
                            description="System stability and latency"
                            columns={['Metric', 'Target', 'Measurement', 'Result']}
                            data={reliabilityData}
                            colorTheme="green"
                        />
                    </div>
                </>
            )}

        </div>
    );
};

export default AdminAIReportPage;
